
import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: boolean;
}


const TrueFalseMySchool: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'TrueFalseMySchool.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/TrueFalseMySchool.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "My School – True or False Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: boolean) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Practice My School True or False questions for UKG students. Simple statements to build awareness of school objects, people, and places in a fun way." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/my-school/true-false" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>My School – True or False</h1>

                                    <p>
                                        Help your UKG child build confidence and understanding of their school environment with this <strong>True or False worksheet</strong>. These simple, engaging statements help children identify what is correct or incorrect about school-related concepts.
                                    </p>

                                    <h2>🎯 What Will Kids Learn?</h2>
                                    <ul>
                                        <li>Recognize school-related places, people, and objects</li>
                                        <li>Understand basic facts about classroom routines and roles</li>
                                        <li>Develop critical thinking through decision-making</li>
                                        <li>Strengthen reading and comprehension skills</li>
                                    </ul>

                                    <h2>✅ Worksheet Highlights</h2>
                                    <p>
                                        Children will read each sentence and decide whether it is <strong>true</strong> or <strong>false</strong>. This activity encourages awareness and logic in a fun, age-appropriate format.
                                    </p>

                                    <h2>📘 Sample Statements</h2>
                                    <ul>
                                        <li>The teacher teaches students. ✅</li>
                                        <li>A bat is used in the classroom. ❌</li>
                                        <li>We eat lunch in the playground. ❌</li>
                                        <li>The school has a blackboard. ✅</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Learning Benefits</h2>
                                    <ul>
                                        <li>Improved knowledge of the school environment</li>
                                        <li>Better logical thinking and reasoning</li>
                                        <li>Easy reading practice for UKG learners</li>
                                        <li>Interactive home or classroom activity</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>My School – True or False</h1>

                                <p>
                                    Help your UKG child build confidence and understanding of their school environment with this <strong>True or False worksheet</strong>. These simple, engaging statements help children identify what is correct or incorrect about school-related concepts.
                                </p>

                                <h2>🎯 What Will Kids Learn?</h2>
                                <ul>
                                    <li>Recognize school-related places, people, and objects</li>
                                    <li>Understand basic facts about classroom routines and roles</li>
                                    <li>Develop critical thinking through decision-making</li>
                                    <li>Strengthen reading and comprehension skills</li>
                                </ul>

                                <h2>✅ Worksheet Highlights</h2>
                                <p>
                                    Children will read each sentence and decide whether it is <strong>true</strong> or <strong>false</strong>. This activity encourages awareness and logic in a fun, age-appropriate format.
                                </p>

                                <h2>📘 Sample Statements</h2>
                                <ul>
                                    <li>The teacher teaches students. ✅</li>
                                    <li>A bat is used in the classroom. ❌</li>
                                    <li>We eat lunch in the playground. ❌</li>
                                    <li>The school has a blackboard. ✅</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Learning Benefits</h2>
                                <ul>
                                    <li>Improved knowledge of the school environment</li>
                                    <li>Better logical thinking and reasoning</li>
                                    <li>Easy reading practice for UKG learners</li>
                                    <li>Interactive home or classroom activity</li>
                                </ul>
                            </article>
                            <div className="mt-2 mb-4">
                                <h3 className="mb-2">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>

                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value!)}>
                                <IonItem
                                    className={`grid-item ${selectedOption === true
                                        ? true === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={true} />
                                    <IonLabel>True</IonLabel>
                                </IonItem>
                                <IonItem
                                    className={`grid-item ${selectedOption === false
                                        ? false === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={false} />
                                    <IonLabel>False</IonLabel>
                                </IonItem>
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default TrueFalseMySchool;
